import { useEffect, useState } from 'react';
import {
    Activity,
    Boxes,
    CheckCircle2,
    FileCheck2,
    FileInput,
    FileOutput,
    PackageCheck,
} from 'lucide-react';
import { PageHeader } from '../Components/data/PageHeader';
import { KpiCard } from '../Components/data/KpiCard';
import { Card, CardHead } from '../Components/ui/Card';
import NexusLayout from '../Layouts/NexusLayout';
import api from '../services/api';

type Movement = {
    id: number;
    tipo: 'entrada' | 'saida';
    data: string;
    descricao: string;
};

type DashboardStats = {
    total_produtos: number;
    entradas_mes: number;
    saidas_mes: number;
    receitas_mes: number;
    inventario_ok: boolean;
    estoque_total: number;
    movimentacao_mensal: Array<{ mes: string; entradas: number; saidas: number }>;
    ultimas_movimentacoes: Movement[];
};

const EMPTY_STATS: DashboardStats = {
    total_produtos: 0,
    entradas_mes: 0,
    saidas_mes: 0,
    receitas_mes: 0,
    inventario_ok: false,
    estoque_total: 0,
    movimentacao_mensal: [],
    ultimas_movimentacoes: [],
};

const FLOW_STEPS = [
    'Confira os dados da farmácia e do responsável técnico.',
    'Mantenha o cadastro de produtos controlados atualizado.',
    'Registre ou confira o inventário de abertura.',
    'Lance entradas, receitas e dispensações do período.',
    'Gere o XML e acompanhe o envio para a ANVISA.',
];

function formatNumber(value: number) {
    return value.toLocaleString('pt-BR', { maximumFractionDigits: 2 });
}

export default function Dashboard() {
    const [stats, setStats] = useState<DashboardStats>(EMPTY_STATS);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        const controller = new AbortController();

        api.get<DashboardStats>('/dashboard', { signal: controller.signal })
            .then((response) => setStats({ ...EMPTY_STATS, ...response.data }))
            .catch((requestError: unknown) => {
                if (controller.signal.aborted) return;
                console.error('Erro ao carregar o dashboard:', requestError);
                setError(true);
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false);
            });

        return () => controller.abort();
    }, []);

    const sparkline = stats.movimentacao_mensal.map(
        (item) => Number(item.entradas) + Number(item.saidas),
    );

    return (
        <NexusLayout title="Dashboard">
            <div className="nx-page nx-sngpc-dashboard">
                <PageHeader
                    icon={<Activity size={21} strokeWidth={2} />}
                    title="Visão geral do SNGPC"
                    description="Acompanhe o estoque controlado e as obrigações do período."
                />

                {error ? (
                    <div className="nx-sngpc-error" role="alert">
                        Não foi possível atualizar os indicadores. Os valores exibidos podem estar desatualizados.
                    </div>
                ) : null}

                <div className="nx-kpi-grid nx-kpi-grid--5">
                    <KpiCard
                        title="Produtos controlados"
                        value={loading ? '...' : formatNumber(stats.total_produtos)}
                        icon={PackageCheck}
                        trendLabel="itens cadastrados"
                    />
                    <KpiCard
                        title="Entradas no mês"
                        value={loading ? '...' : formatNumber(stats.entradas_mes)}
                        icon={FileInput}
                        iconBg="#e8f1ff"
                        iconColor="#2563eb"
                        sparkline={sparkline}
                        trendLabel="movimentações"
                    />
                    <KpiCard
                        title="Saídas no mês"
                        value={loading ? '...' : formatNumber(stats.saidas_mes)}
                        icon={FileOutput}
                        iconBg="#fff1e8"
                        iconColor="#c2410c"
                        sparkline={sparkline}
                        trendLabel="dispensações"
                    />
                    <KpiCard
                        title="Receitas no mês"
                        value={loading ? '...' : formatNumber(stats.receitas_mes)}
                        icon={FileCheck2}
                        iconBg="#f0eaff"
                        iconColor="#6d28d9"
                        trendLabel="prescrições"
                    />
                    <KpiCard
                        title="Saldo em estoque"
                        value={loading ? '...' : formatNumber(stats.estoque_total)}
                        icon={Boxes}
                        iconBg="#e8f8ef"
                        iconColor="#15803d"
                        trendLabel="unidades"
                    />
                </div>

                <div className="nx-sngpc-dashboard-grid">
                    <Card>
                        <CardHead
                            title="Fluxo operacional"
                            actions={
                                <span className={`nx-sngpc-inventory-state ${stats.inventario_ok ? 'is-ok' : ''}`}>
                                    <CheckCircle2 size={15} />
                                    Inventário {stats.inventario_ok ? 'em dia' : 'pendente'}
                                </span>
                            }
                        />
                        <ol className="nx-sngpc-flow">
                            {FLOW_STEPS.map((step, index) => (
                                <li key={step}>
                                    <span>{index + 1}</span>
                                    <p>{step}</p>
                                </li>
                            ))}
                        </ol>
                    </Card>

                    <Card>
                        <CardHead title="Últimas movimentações" />
                        {stats.ultimas_movimentacoes.length === 0 ? (
                            <div className="nx-sngpc-panel-empty">
                                Nenhuma movimentação recente.
                            </div>
                        ) : (
                            <ul className="nx-sngpc-activity-list">
                                {stats.ultimas_movimentacoes.map((movement) => (
                                    <li key={`${movement.tipo}-${movement.id}`}>
                                        <span className={`nx-sngpc-activity-icon is-${movement.tipo}`}>
                                            {movement.tipo === 'entrada' ? (
                                                <FileInput size={15} />
                                            ) : (
                                                <FileOutput size={15} />
                                            )}
                                        </span>
                                        <div>
                                            <strong>{movement.descricao}</strong>
                                            <span>{new Date(movement.data).toLocaleDateString('pt-BR')}</span>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </Card>
                </div>
            </div>
        </NexusLayout>
    );
}
